import { Image, imageAsset } from "../../assets/images";
import { TextViewer } from "@etlyn/etlyn-cms";
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PublicAPI } from "../../api";
import { Footer } from "../../components/Footer";
import { SEOHead } from "../../components/SEOHead";
import { readBlogBootstrap } from "./blogBootstrap";

/* ── helpers ──────────────────────────────────────────────── */

function getTextStats(html: string): { wordCount: number; readingTime: number } {
  if (!html) return { wordCount: 0, readingTime: 1 };
  const plain = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const words = plain.split(/\s+/).filter(Boolean);
  return {
    wordCount: words.length,
    readingTime: Math.max(1, Math.ceil(words.length / 225)),
  };
}
const hasBody = (html?: string) => Boolean(html?.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/g, ''));

function formatDate(raw?: string | number): string {
  if (!raw) return "";
  const d = new Date(typeof raw === "number" ? raw * 1000 : raw);
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function isoDate(raw?: string | number): string | undefined {
  if (!raw) return undefined;
  const value = new Date(typeof raw === "number" ? raw * 1000 : raw);
  return Number.isFinite(value.getTime()) ? value.toISOString() : undefined;
}

/* ── component ────────────────────────────────────────────── */

function BlogPost() {
  const { blogSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(() => readBlogBootstrap('article', blogSlug));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const bootstrap = readBlogBootstrap('article', blogSlug);
    if (bootstrap) {
      setData(bootstrap);
      setFailed(false);
    } else setData(undefined);
    setFailed(false);
    PublicAPI.get(`/blogs/${encodeURIComponent(blogSlug || "")}`, { signal: controller.signal })
      .then((res: any) => { if (active) { if (res.data?.title && res.data.published === true) setData(res.data); else setFailed(true); } })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; controller.abort(); };
  }, [blogSlug]);

  const [device, setDevice] = useState("web");
  useEffect(() => {
    if (window.screen.width < 768) setDevice("mob");
  }, []);

  const isMob = device === "mob";
  const stats = useMemo(() => getTextStats(hasBody(data?.content_en) ? data.content_en : data?.content || ""), [data?.content, data?.content_en]);

  if (failed) {
    return <main className="max-w-3xl mx-auto px-4 mt-[120px] py-12">
      <SEOHead title="Article unavailable" description="This article is not available." path={`/blogs/${encodeURIComponent(blogSlug || '')}`} robots="noindex,follow" />
      <h1 className="text-2xl font-semibold">Article unavailable</h1>
      <p role="alert" className="my-4">We couldn’t load this article. Please try again later.</p>
      <a href="/blogs" className="underline">Back to all articles</a>
    </main>;
  }

  if (!data) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-4 mt-[80px] font-['Inter',sans-serif]">
        <div className="py-32 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-[#0D2B7E] border-t-transparent" />
          <p className="mt-4 text-sm text-[#808692]">Loading article…</p>
        </div>
      </div>
    );
  }

  const english = hasBody(data.content_en);
  const title = (english && data.title_en?.trim()) || data.title;
  const content = english ? data.content_en : data.content;
  const description = data.seo_description || (english && data.excerpt_en) || data.excerpt ||
    (content ? content.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160) : "Read this article on the Etlyn blog.");
  const path = `/blogs/${encodeURIComponent(data.slug || data.id || blogSlug || '')}`;
  const published = isoDate(data.published_at);
  const modified = isoDate(data.updated_at) || published;
  const author = (english && data.author_en) || (typeof data.author === 'string' ? data.author : data.author?.name) || "Etlyn";

  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 overflow-x-hidden mt-[80px] font-['Inter',sans-serif]">
      <SEOHead
        title={data.seo_title || title || "Blog Post"}
        description={description}
        path={path}
        type="article"
        image={imageAsset(data.cover_image_url)?.url || data.cover_image_url || undefined}
        publishedTime={published}
        modifiedTime={modified}
        author={author}
        keywords={data.category ? `${data.category}, software development, Etlyn blog` : "software development, Etlyn blog"}
        jsonLd={{ "@context": "https://schema.org", "@graph": [{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: title,
          description,
          image: imageAsset(data.cover_image_url)?.url || data.cover_image_url || "",
          author: { "@type": author === "Etlyn" ? "Organization" : "Person", name: author },
          publisher: { "@type": "Organization", name: "Etlyn", url: "https://www.etlyn.com" },
          ...(published ? {datePublished: published} : {}),
          ...(modified ? {dateModified: modified} : {}),
          mainEntityOfPage: `https://www.etlyn.com${path}`,
        }, { "@type": "BreadcrumbList", itemListElement: [
          {name: "Home", item: "https://www.etlyn.com/"},
          {name: "Blog", item: "https://www.etlyn.com/blogs"},
          {name: title, item: `https://www.etlyn.com${path}`},
        ].map((item, index) => ({"@type": "ListItem", position: index + 1, ...item})) }] }}
      />

      {/* ─── Back link ───────────────────────────────────── */}
      <div className="pt-6 pb-4">
        <button
          onClick={() => navigate("/blogs")}
          className="inline-flex items-center gap-1.5 text-sm text-[#666D7C] hover:text-[#0D2B7E] transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
          </svg>
          Back to all articles
        </button>
      </div>

      {/* ─── Article ─────────────────────────────────────── */}
      <article className={`mx-auto ${isMob ? "max-w-full" : "max-w-3xl"}`}>
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          {data.category && (
            <span className="inline-block rounded-full bg-[#EEF1FB] px-3 py-1 text-[11px] font-semibold text-[#0D2B7E] uppercase tracking-wide">
              {data.category}
            </span>
          )}
          {published && <time dateTime={published} className="text-xs text-[#808692]">
            {formatDate(published)}
          </time>}
          <span className="text-xs text-[#808692] flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            {stats.readingTime} min read
          </span>
        </div>

        {/* Title */}
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#333D51] leading-tight tracking-tight mb-6">
            {title}
        </h1>

        {/* Author row */}
        {author && (
          <div className="flex items-center gap-3 mb-8 pb-6 border-b border-[#DFDFE0]/60">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#0D2B7E] to-[#25408B] text-xs font-bold text-white">
              {author.charAt(0).toUpperCase()}
            </span>
            <div>
              <p className="text-sm font-medium text-[#333D51]">{author}</p>
              <p className="text-xs text-[#808692]">{stats.wordCount.toLocaleString()} words</p>
            </div>
          </div>
        )}

        {/* Cover image */}
        {data.cover_image_url && (
          <div className="relative overflow-hidden rounded-2xl mb-10">
            <Image
              src={data.cover_image_url}
              priority sizes="(max-width: 800px) 100vw, 768px"
              alt={title}
              className="w-full object-cover"
              style={{ maxHeight: 480 }}
            />
          </div>
        )}

        {/* Body */}
        <div className="prose-etlyn mb-16">
          <TextViewer htmlContent={content} />
        </div>

        {/* Tags */}
        {data.tags && data.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-10 pb-8 border-b border-[#DFDFE0]/60">
            <span className="text-xs font-medium text-[#808692] mr-1">Tags:</span>
            {data.tags.map((tag: string) => (
              <span
                key={tag}
                className="inline-block rounded-full bg-[#F2F3F5] px-3 py-1 text-[11px] font-medium text-[#666D7C]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Share / CTA */}
        <div className="rounded-2xl bg-[#F8F9FB] ring-1 ring-[#DFDFE0]/60 p-6 sm:p-8 text-center mb-16">
          <h3 className="text-base font-semibold text-[#333D51] mb-2">
            Enjoyed this article?
          </h3>
          <p className="text-sm text-[#666D7C] mb-5 max-w-md mx-auto leading-relaxed">
            We write about technology, growth strategies and digital product
            development. Get in touch to discuss how we can help your business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/home#contact")}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#0D2B7E] to-[#25408B] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              Start a Conversation
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </button>
            <button
              onClick={() => navigate("/blogs")}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#DFDFE0] px-5 py-2.5 text-sm font-medium text-[#333D51] transition hover:border-[#0D2B7E] hover:text-[#0D2B7E]"
            >
              Read More Articles
            </button>
          </div>
        </div>
      </article>

      <Footer isMob={isMob} />
    </div>
  );
}

export default BlogPost;
